import { EventType, ScheduleCard, ScheduleStatus } from "./components/scheduler/types";
import Scheduler from "./components/scheduler/Scheduler";
import { EventTypeInfo } from "./components/scheduler/constants";
import { Dialog, DialogContent } from "@altimateai/lego";
import { useMemo, useState } from "react";
import AddScheduleForm from "./components/schedulerForms/AddScheduleForm";
import { transformWarehouseSizeString } from "./components/scheduler/helper";
import { useScheduleCardsWithDefaults } from "./components/scheduler/useScheduleCardsWithDefaults";
import { useAppState } from "@/modules/app/useAppContext";
import { RecurrencePattern, ScheduleResponse } from "./types";
import dayjs from "dayjs";
import useWarehouseScheduler from "./components/scheduler/useWarehouseScheduler";
import { isOwner, isAdmin } from "@/services/auth";

export function getEventTypeFromApiResponse(eventType: string): string {
  return Object.keys(EventTypeInfo).find(
    key => EventTypeInfo[key as EventType].backendLabel === eventType
  )!;
}

function mapSchedulerDataToScheduleCard(
  schedulerData: ScheduleResponse,
  index: number,
  arr: ScheduleResponse[]
): ScheduleCard {
  const WarehouseSizeMap: { [key: string]: string } = {
    "X-Small": "XS",
    Small: "S",
    Medium: "M",
    Large: "L",
    "X-Large": "XL",
    "2X-Large": "2XL",
    "3X-Large": "3XL",
    "4X-Large": "4XL",
    "5X-Large": "5XL",
    "6X-Large": "6XL",
  };
  const prev = arr[index - 1];
  let change: "upsize" | "downsize" | "no_change" | "daybreak" = "no_change";
  if (prev) {
    const prevSize = prev.configuration?.warehouse_size;
    const currSize = schedulerData?.configuration?.warehouse_size;
    const prevSizeIndex = Object.keys(WarehouseSizeMap).indexOf(prevSize ?? "");
    const currSizeIndex = Object.keys(WarehouseSizeMap).indexOf(currSize ?? "");
    if (prevSizeIndex > currSizeIndex) {
      change = "downsize";
    }
    if (prevSizeIndex < currSizeIndex) {
      change = "upsize";
    }
    if (
      prevSizeIndex === currSizeIndex &&
      dayjs(prev.end_time).isBefore(dayjs(schedulerData.start_time), "day")
    ) {
      change = "daybreak";
    }
    if (
      prevSizeIndex === currSizeIndex &&
      !dayjs(prev.end_time).isBefore(dayjs(schedulerData.start_time), "day")
    ) {
      change = "no_change";
    }
  }

  const reasoning: string[] | { timestamp: string; reason: string }[] | undefined =
    schedulerData.reasons && schedulerData.reasons.length > 0
      ? schedulerData.reasons
      : schedulerData.reason
        ? [schedulerData.reason]
        : undefined;

  return {
    id: schedulerData.schedule_id.toString(),
    title: schedulerData.warehouse_name,
    start: schedulerData.start_time,
    end: schedulerData.end_time,
    type: getEventTypeFromApiResponse(schedulerData.schedule_type) as EventType,
    reasoning,
    sizeFrom: (schedulerData?.defaults?.configuration as Record<string, unknown>)
      ?.warehouse_size as string | undefined,
    sizeTo:
      (schedulerData?.recommendation?.recommendation_data?.warehouse_size as string) ||
      (schedulerData?.configuration?.warehouse_size as string),
    change,
    clusterMin: (schedulerData?.defaults?.configuration as Record<string, unknown>)
      ?.cluster_count as number | undefined,
    clusterMax: schedulerData?.recommendation?.recommendation_data?.cluster_count as number,
    savings:
      ((schedulerData?.recommendation?.metrics as Record<string, unknown>)?.potential_savings as
        | string
        | undefined) || "0",
    status: schedulerData?.status as ScheduleStatus,
    recurrencePattern: schedulerData.recurrence_pattern as RecurrencePattern | undefined,
    created_by: schedulerData?.created_by,
  };
}

export const WarehouseScheduler = ({
  instance_id,
  warehouse_size,
  warehouse_name,
  isSchedulingEnabled,
  isDynamic = false,
  startDate,
  endDate,
}: {
  instance_id: string;
  warehouse_size: string;
  warehouse_name: string;
  isSchedulingEnabled: boolean;
  isDynamic?: boolean;
  startDate?: Date;
  endDate?: Date;
}) => {
  const { showDefaultScheduleBlocks } = useAppState();
  const canSchedule = isOwner() || isAdmin();
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<{
    startDate?: string;
    endDate?: string;
  }>({
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
  });

  // by default getting the last 30 days and next 30 days data
  // temporary fix for the date range as changing date range from the scheduler is not working
  // due to rerenders
  // will fix during refactoring to use the date range from the scheduler and only fetch set amount of data
  // TODO: refactor the date range to use the date range from the scheduler
  const [warehouseScheduleCalenderStartDate, setWarehouseScheduleCalenderStartDate] = useState<
    Date | undefined
  >(undefined);
  const [warehouseScheduleCalenderEndDate, setWarehouseScheduleCalenderEndDate] = useState<
    Date | undefined
  >(undefined);

  const { data: schedulerData, isFetching: isSchedulerFetching } = useWarehouseScheduler({
    instance_id,
    warehouse_name,
    start_time:
      warehouseScheduleCalenderStartDate?.toISOString() ||
      dayjs().subtract(30, "day").toISOString(),
    end_time:
      warehouseScheduleCalenderEndDate?.toISOString() || dayjs().add(30, "day").toISOString(),
    isDynamic: isDynamic,
    enabled:
      Boolean(warehouseScheduleCalenderStartDate) && Boolean(warehouseScheduleCalenderEndDate),
  });

  const handleAddScheduler = ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    setShowSidebar(true);
    const now = dayjs();

    if (!startDate) {
      setSelectedDateRange({
        startDate: dayjs().add(1, "hour").startOf("hour").toISOString(),
        endDate: now.add(2, "hour").startOf("hour").toISOString(),
      });
      return;
    }

    const adjustedStartDate = dayjs(startDate).isBefore(now)
      ? dayjs().add(1, "hour").startOf("hour").toISOString()
      : startDate;

    const adjustedEndDate = endDate
      ? dayjs(endDate).isBefore(now)
        ? now.add(1, "hour").toISOString()
        : endDate
      : dayjs(adjustedStartDate).add(1, "hour").toISOString();

    setSelectedDateRange({
      startDate: adjustedStartDate,
      endDate: adjustedEndDate,
    });
  };

  const scheduleCards = useMemo(() => {
    return schedulerData?.map(mapSchedulerDataToScheduleCard) || [];
  }, [schedulerData]);

  const warehouseSize = transformWarehouseSizeString(scheduleCards[0]?.sizeFrom || warehouse_size);

  const cardsWithDefaults = useScheduleCardsWithDefaults({
    cards: scheduleCards,
    viewStart: warehouseScheduleCalenderStartDate,
    viewEnd: warehouseScheduleCalenderEndDate,
    defaultWarehouseSize: warehouseSize,
    enabled: showDefaultScheduleBlocks ?? false,
  });

  return (
    <div>
      <Scheduler
        warehouseName={warehouse_name}
        instanceId={instance_id}
        cards={cardsWithDefaults}
        addScheduleFunction={handleAddScheduler}
        isSchedulingEnabled={isSchedulingEnabled}
        defaultWarehouseSize={warehouseSize}
        isDynamicSchedule={isDynamic}
        canSchedule={canSchedule}
        onCalenderDatesChange={(start, end) => {
          setWarehouseScheduleCalenderStartDate(start);
          setWarehouseScheduleCalenderEndDate(end);
        }}
        isLoading={isSchedulerFetching}
      />
      <Dialog open={showSidebar} onOpenChange={open => setShowSidebar(open)}>
        <DialogContent aria-label="Add schedule form dialog">
          <AddScheduleForm
            isDynamic={isDynamic}
            warehouseName={warehouse_name}
            instanceId={instance_id}
            warehouseSize={warehouseSize}
            defaultWarehouseSize={warehouseSize}
            handleClose={() => setShowSidebar(false)}
            selectedDateTime={selectedDateRange.startDate}
            selectedEndDateTime={selectedDateRange.endDate}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
